import {
  type LogisticsPrediction,
  type PredictionBoard,
  PredictionOutcome,
  type ShipmentCard,
  type ShipmentPlan,
} from "#/types/prediction";
import { formatWeekday, weekdayShortName } from "#/utils/dateTimeUtils";

const DAY_MS = 86_400_000;

/**
 * Where a prediction sits against the current time. The Predicted tab is a
 * week calendar of shipments and never needs this: "overdue" and "quiet"
 * drive the wording on the prediction detail page, which opens History rows
 * too; "upcoming" is everything else.
 */
export type PredictionColumn = "overdue" | "upcoming" | "quiet";

/** Decimal columns arrive as strings from the API. */
export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Whole calendar days (UTC) from `from` to `to`, negative when `to` is earlier.
 * Due dates are days anchored at noon UTC (see the engine), so every comparison
 * here is by calendar day, never by the hour.
 */
export function daysBetween(from: Date | string, to: Date | string): number {
  const a = new Date(from);
  const b = new Date(to);
  return Math.round(
    (Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()) -
      Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate())) /
      DAY_MS
  );
}

/**
 * Where a prediction sits right now. Late and "call sub-client" are not stored
 * with the prediction: they follow from its due window against the current time.
 */
export function columnOf(prediction: LogisticsPrediction, now: Date): PredictionColumn {
  if (prediction.quietAfter && daysBetween(prediction.quietAfter, now) > 0) return "quiet";
  if (prediction.dueTo && daysBetween(prediction.dueTo, now) > 0) return "overdue";
  return "upcoming";
}

/** What happened to a prediction whose day has passed, as the page words it. */
export type OutcomeWord = "delivered" | "late" | "early" | "not_delivered" | "pending";

/**
 * The outcome of a prediction against the clock: delivered early / on time /
 * late once the next visit was seen; not delivered when the window has passed
 * with nothing seen; pending while the window is still open.
 */
export function outcomeOf(prediction: LogisticsPrediction, now: Date): OutcomeWord {
  switch (prediction.outcome) {
    case PredictionOutcome.ON_TIME:
      return "delivered";
    case PredictionOutcome.EARLY:
      return "early";
    case PredictionOutcome.LATE:
      return "late";
    default:
      return lateDays(prediction, now) > 0 ? "not_delivered" : "pending";
  }
}

/** True when the shipment's day is before today (local calendar day). */
export function isPastShipment(card: ShipmentCard, now: Date): boolean {
  return daysBetween(card.shipment.shipmentDate, now) > 0;
}

/** Days past the end of the due window; 0 when not late. */
export function lateDays(prediction: LogisticsPrediction, now: Date): number {
  if (!prediction.dueTo) return 0;
  return Math.max(0, daysBetween(prediction.dueTo, now));
}

export function categoryName(
  board: Pick<PredictionBoard, "categories">,
  productId: number
): string {
  return board.categories.find((category) => category.id === productId)?.name ?? `#${productId}`;
}

export function categoryColor(
  board: Pick<PredictionBoard, "categories">,
  productId: number
): string | null {
  return board.categories.find((category) => category.id === productId)?.color ?? null;
}

/** The prediction the shipment's date was set from (shortest gap), else the first stop. */
export function anchorOf(card: ShipmentCard): LogisticsPrediction | undefined {
  return (
    card.predictions.find((prediction) => prediction.id === card.shipment.anchorPredictionId) ??
    card.predictions[0]
  );
}

/** Minutes as "2 h 30 min" / "45 min"; "--" when unknown. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return "--";
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** Two decimals at most, no trailing zeros; "--" when unknown. */
export function formatMeasure(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "--";
  return String(Math.round(value * 100) / 100);
}

/** `part` as a whole percentage of `whole`, e.g. "70 %"; "--" when either is unknown or the whole is 0. */
export function percentOf(
  part: number | null | undefined,
  whole: number | null | undefined
): string {
  if (
    part === null ||
    part === undefined ||
    whole === null ||
    whole === undefined ||
    !Number.isFinite(part) ||
    !Number.isFinite(whole) ||
    whole <= 0
  ) {
    return "--";
  }
  return `${Math.round((part / whole) * 100)} %`;
}

/** ISO weekday (1 = Monday … 7 = Sunday) of a date, by its UTC day, as the backend anchors days. */
export function isoWeekdayOf(date: string | Date): number {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 0;
  return ((parsed.getUTCDay() + 6) % 7) + 1;
}

/** Length of a driver's shift in minutes from "HH:mm" start and end; null when either is malformed. */
export function shiftMinutes(driver: { startTime: string; endTime: string }): number | null {
  const toMinutes = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value ?? "");
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
  };
  const start = toMinutes(driver.startTime);
  const end = toMinutes(driver.endTime);
  if (start === null || end === null || end <= start) return null;
  return end - start;
}

type Translate = (key: string, values?: Record<string, string | number>) => string;

/** One line of the reasons box; `children` are shown as nested bullets. */
export interface WhyLine {
  text: string;
  children?: string[];
}

/**
 * The plain-fact reasons behind the shipment's trips, one paragraph for the
 * whole card, shown only when something needs one: the load was split over
 * trucks or trips, a trip is on a later day, a truck has no driver, a stop is
 * too big for the largest truck. Facts only (weights, limits, hours, days),
 * never the method.
 */
export function shipmentWhy(
  plan: ShipmentPlan,
  shipmentDate: string,
  t: Translate,
  language: string
): WhyLine[] {
  const why: WhyLine[] = [];
  const say = (key: string, values?: Record<string, string | number>) =>
    why.push({ text: t(`predictedDeliveries.shipment.trips.why.${key}`, values) });
  const trucks = [...new Set(plan.trips.map((trip) => trip.truck.vehicleId))];

  // 0. Where trucks and drivers come from.
  if (plan.origin) say("siteOnly", { site: plan.origin.name });

  // 1. Why the load was split over trucks.
  if (trucks.length > 1) {
    say("sharedTrucks", {
      kg: formatMeasure(plan.total.kg),
      m3: formatMeasure(plan.total.m3),
      count: trucks.length,
    });
  }

  // 2. Why a truck drives more than one trip.
  for (const vehicleId of trucks) {
    const truckTrips = plan.trips.filter((trip) => trip.truck.vehicleId === vehicleId);
    if (truckTrips.length < 2) continue;
    const { truck } = truckTrips[0];
    const kg = truckTrips.reduce<number | null>(
      (sum, trip) => (sum === null || trip.kg === null ? null : sum + trip.kg),
      0
    );
    const m3 = truckTrips.reduce<number | null>(
      (sum, trip) => (sum === null || trip.m3 === null ? null : sum + trip.m3),
      0
    );
    const values = { truck: truck.name, count: truckTrips.length };
    if (kg !== null && truck.payloadKg !== null && kg > truck.payloadKg) {
      say("splitWeight", {
        ...values,
        total: formatMeasure(kg),
        payload: formatMeasure(truck.payloadKg),
      });
    } else if (m3 !== null && truck.capacityM3 !== null && m3 > truck.capacityM3) {
      say("splitSpace", {
        ...values,
        total: formatMeasure(m3),
        capacity: formatMeasure(truck.capacityM3),
      });
    } else {
      say("splitHours", values);
    }
  }

  // 3. Why a trip is on a later day.
  const shipmentWeekday = isoWeekdayOf(shipmentDate);
  for (const trip of plan.trips) {
    if (trip.dayOffset === 0 || !trip.driver) continue;
    const anyoneWorksShipmentDay = trip.truck.drivers.some((driver) =>
      driver.workingDays.includes(shipmentWeekday)
    );
    const only = trip.truck.drivers.length === 1 ? "Only" : "";
    const values = {
      index: trip.index,
      weekday: formatWeekday(trip.day, language),
      driver: trip.driver.name,
    };
    if (anyoneWorksShipmentDay) {
      say(`laterDayHours${only}`, {
        ...values,
        hours: formatMinutes(shiftMinutes(trip.driver)),
      });
    } else {
      say(`laterDayWeekday${only}`, {
        ...values,
        days: trip.driver.workingDays.map((day) => weekdayShortName(day, language)).join(", "),
      });
    }
  }

  // 4. What the plan could not check or carry.
  for (const vehicleId of trucks) {
    const trip = plan.trips.find((candidate) => candidate.truck.vehicleId === vehicleId)!;
    if (trip.reason === "no_driver") say("noDriver", { truck: trip.truck.name });
  }
  for (const trip of plan.trips) {
    if (trip.reason === "exceeds_truck")
      say("exceedsTruck", { index: trip.index, runs: trip.runs });
  }

  // 5. The trucks at the Site that were not picked, one nested line each with
  //    the reason. Drivers never decide the truck; a missing driver is noted
  //    as a fact only.
  if (plan.trips.length > 0) {
    const used = new Set(trucks);
    const loadedStops = plan.stops.filter((stop) => stop.units > 0);
    const unused = plan.trucks.filter((truck) => !used.has(truck.vehicleId));
    if (unused.length > 0) {
      const children = unused.map((truck) => {
        const values = {
          truck: truck.name,
          kg: formatMeasure(truck.payloadKg),
          m3: formatMeasure(truck.capacityM3),
        };
        let key = "unusedTooSmall";
        if (truck.payloadKg === null || truck.capacityM3 === null) key = "unusedUnsized";
        else if (truck.fitsAll) {
          // The chosen truck is the smallest that carries everything; an equal one lost the tie by name.
          const sameSize = plan.trips.some(
            (trip) =>
              trip.truck.payloadKg === truck.payloadKg && trip.truck.capacityM3 === truck.capacityM3
          );
          key = sameSize ? "unusedSameSize" : "unusedBigger";
        } else if (
          loadedStops.some(
            (stop) =>
              stop.kg !== null &&
              stop.m3 !== null &&
              stop.kg <= truck.payloadKg! &&
              stop.m3 <= truck.capacityM3!
          )
        ) {
          key = "unusedNotNeeded";
        }
        const reason = t(`predictedDeliveries.shipment.trips.why.${key}`, values);
        return truck.drivers.length === 0
          ? `${reason} ${t("predictedDeliveries.shipment.trips.why.unusedNoDriver")}`
          : reason;
      });
      why.push({ text: t("predictedDeliveries.shipment.trips.why.unusedTitle"), children });
    }
  }
  return why;
}
